/**
 * Customer API Validators
 * Zod schemas for customer and address validation
 */

import { z } from 'zod';
import { isPostalCodeRequired } from '@/lib/countries';
import {
  sortOrderEnum,
  paginationQuerySchema,
  emailSchema,
  passwordSchema,
} from './common.validator';

// Re-export common schemas for backwards compatibility
export { sortOrderEnum, emailSchema, passwordSchema };

// Address type enum
export const addressTypeEnum = z.enum(['shipping', 'billing']);
export type AddressType = z.infer<typeof addressTypeEnum>;

// Sort options for customer list
export const customerSortEnum = z.enum(['createdAt', 'email', 'lastName']);

/**
 * Phone validation - flexible format
 */
export const phoneSchema = z.string().max(50).optional();

// ============================================================================
// Address Schemas
// ============================================================================

/**
 * Address fields shared by create and update schemas. State and postal code
 * are optional at the field level — many countries have no region and some
 * (e.g. Sierra Leone) have no postal-code system; the create schema's
 * superRefine requires them per country.
 */
const addressFieldsSchema = z.object({
  type: addressTypeEnum,
  isDefault: z.boolean().default(false),
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  company: z.string().max(255).optional(),
  phone: phoneSchema,
  street1: z.string().min(1, 'Street address is required').max(255),
  street2: z.string().max(255).optional(),
  city: z.string().min(1, 'City is required').max(100),
  state: z.string().max(100).optional().default(''),
  postalCode: z.string().max(20).optional().default(''),
  country: z.string().length(2).default('US'),
});

/**
 * Address input for creating addresses
 */
export const addressInputSchema = addressFieldsSchema.superRefine((addr, ctx) => {
  if (addr.country.toUpperCase() === 'US' && !addr.state.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['state'],
      message: 'State is required',
    });
  }
  if (isPostalCodeRequired(addr.country) && !addr.postalCode.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['postalCode'],
      message: 'Postal code is required',
    });
  }
});

export type AddressInput = z.infer<typeof addressInputSchema>;

/**
 * Partial address for updates
 */
export const addressUpdateSchema = addressFieldsSchema.partial().extend({
  type: addressTypeEnum.optional(),
});

export type AddressUpdate = z.infer<typeof addressUpdateSchema>;

// ============================================================================
// Customer Schemas
// ============================================================================

/**
 * Create customer input
 */
export const createCustomerSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  firstName: z.string().max(100).optional(),
  lastName: z.string().max(100).optional(),
  phone: phoneSchema,
  company: z.string().max(255).optional(),
  customerGroupId: z.coerce.number().int().positive().optional(),
});

export type CreateCustomerInput = z.infer<typeof createCustomerSchema>;

/**
 * Self-service profile update (PATCH /api/customers/[id]).
 *
 * Strict on purpose: the customer's own session posts this, so any key not
 * listed here is a 400. MONEY PATH: `customerGroupId` drives checkout pricing
 * (group_prices), so it must never be writable here; group assignment is
 * admin-only (partner application approval, admin customer routes).
 * `emailVerified` is set only by the email-change flow on the server.
 */
export const updateCustomerSchema = z
  .object({
    firstName: z.string().max(100).optional(),
    lastName: z.string().max(100).optional(),
    phone: phoneSchema,
    company: z.string().max(255).optional(),
  })
  .strict();

export type UpdateCustomerInput = z.infer<typeof updateCustomerSchema>;

/**
 * Customer login input
 */
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
});

export type LoginInput = z.infer<typeof loginSchema>;

/**
 * Password change input
 */
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Current password is required'),
  newPassword: passwordSchema,
});

export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

// ============================================================================
// Query Schemas
// ============================================================================

/**
 * Customer list query params
 */
export const customerListQuerySchema = paginationQuerySchema.extend({
  search: z.string().max(100).optional(),
  customerGroupId: z.coerce.number().int().positive().optional(),
  sort: customerSortEnum.default('createdAt'),
  order: sortOrderEnum.default('desc'),
});

export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;

// ============================================================================
// Path Parameter Schemas
// ============================================================================

/**
 * Customer ID path param
 */
export const customerIdParamSchema = z.object({
  id: z.coerce.number().int().positive(),
});

export type CustomerIdParam = z.infer<typeof customerIdParamSchema>;

/**
 * Address ID path param
 */
export const addressIdParamSchema = z.object({
  addressId: z.coerce.number().int().positive(),
});

export type AddressIdParam = z.infer<typeof addressIdParamSchema>;

/**
 * Customer and Address ID combined path params
 */
export const customerAddressParamSchema = z.object({
  id: z.coerce.number().int().positive(),
  addressId: z.coerce.number().int().positive(),
});

export type CustomerAddressParam = z.infer<typeof customerAddressParamSchema>;
